import {useEffect, useRef} from 'react'
import {getAuth, onAuthStateChanged} from 'firebase/auth'
import {authFetch} from '../utils/cloudFunctionsAuth'

const INIT_GRAPH_PUSH_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/initGraphPush'

// We want Graph push to always be on, but avoid spamming Cloud Functions on every route change / refresh.
// This runs once per tab session (sessionStorage) after Firebase auth is ready.
const SESSION_KEYS = {
  lastAttemptMs: 'graphPushAutoInit:lastAttemptMs',
  disabledThisSession: 'graphPushAutoInit:disabledThisSession',
}

function getSessionNumber(key: string): number | null {
  try {
    const v = sessionStorage.getItem(key)
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function setSessionString(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

function setSessionNumber(key: string, value: number) {
  setSessionString(key, String(value))
}

/**
 * Side-effect component that ensures Microsoft Graph push subscriptions are initialized/renewed automatically.
 * Safe to render anywhere (no UI).
 */
export function GraphPushAutoInit() {
  const inFlight = useRef(false)

  useEffect(() => {
    const auth = getAuth()

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return
      if (inFlight.current) return

      const disabled = (() => {
        try {
          return sessionStorage.getItem(SESSION_KEYS.disabledThisSession) === '1'
        } catch {
          return false
        }
      })()
      if (disabled) return

      // Attempt once per tab session, unless the last attempt was very recent (fast reloads).
      const lastAttemptMs = getSessionNumber(SESSION_KEYS.lastAttemptMs)
      if (typeof lastAttemptMs === 'number' && Date.now() - lastAttemptMs < 5 * 60 * 1000) {
        return
      }

      inFlight.current = true
      setSessionNumber(SESSION_KEYS.lastAttemptMs, Date.now())

      try {
        const resp = await authFetch(INIT_GRAPH_PUSH_URL, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({}),
        })

        // If this user isn't allowed (owner-guarded), disable further attempts this session.
        if (resp.status === 401 || resp.status === 403) {
          setSessionString(SESSION_KEYS.disabledThisSession, '1')
          return
        }

        // Swallow errors; the scheduled renew job remains as fallback.
        await resp.json().catch(() => ({}))
      } catch {
        // ignore
      } finally {
        inFlight.current = false
      }
    })

    return () => unsub()
  }, [])

  return null
}


